import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import _, { isEqual } from 'lodash';
import { Button, Dropdown, Modal } from 'react-bootstrap';
import { AnalyticsDebug } from '../Analytics';
import { AssetContentViewer } from '../viewer';
import useDelayedInView from '../../../components/DelayInView';
import Content from '../../../components/widgets/core/content';
import FileService from '../helper/fileHelper';
import { GatingContext, WidgetsContext } from '../../../components/widgets/core/context';
import { getContentForFieldSelector } from '../fields';
import styles from '../assetviewer.module.scss';

export function LightBoxViewerModal({ 
  scrollContainerRef, fullscreen, isPreview, show, handleClose, activeAsset, initialContent, gatingContext, 
  state, handleAssetLoaded, handleAssetVisibilityChange 
}) {

    const asset = initialContent?.type === 'ASSET' ? initialContent : null;
    const active = asset?.uid == _.get(activeAsset, 'uid') && !gatingContext.gating;
    const preload = state.preloadedAssets.find(item => item === asset?.uid) != null;
    const isDarkMode = _.get(asset, 'darkViewer') || false;

  return (
    <Modal
      ref={scrollContainerRef}
      backdrop
      className={styles.assetViewerModal}
      contentClassName={cx(styles.assetViewerContent, styles.headerVisible, {
        [styles.fullscreen]: fullscreen
      }, isDarkMode ? styles.darkViewerContent : styles.lightViewerContent)}
      dialogClassName={cx(styles.assetViewerDialog, {
        [styles.fullscreen]: fullscreen,
        [styles.previewDialog]: isPreview
      })}
      fullscreen={fullscreen}
      backdropClassName={styles.backdropWrapper}
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      enforceFocus={false}
    >
      <Modal.Body className={cx(
          styles.assetViewerBody, 
          styles.singleAssetViwerBody, 
          isDarkMode ? styles.darkViewerBody : styles.lightViewerBody
        )}
      >      
        <div className={cx(
            styles.assetsContainer, 
            styles.assetsContainerLightBox, 
          )}
        >
          <AssetView
            key={asset?.uid}
            asset={asset}
            isDarkMode={isDarkMode}
            preload={active || preload}
            active={active}
            activeAsset={activeAsset}
            isPreview={isPreview}
            onLoad={handleAssetLoaded}
            onVisibilityChange={handleAssetVisibilityChange}
            onClose={handleClose} 
          />
        </div>
      </Modal.Body>
    </Modal>
  )
};

const ContentMetadata = ({ content, className, isDarkMode }) => {
  const type = getContentForFieldSelector(content, 'contentType');
  const meta = _.get(content, 'content.asset.metadata');
  const metaData = FileService.metaData(meta);

  return (
    <div className={cx(styles.contentMetadata, className, isDarkMode ? styles.darkContentMetadata : styles.lightContentMetadata)}>
       <div className={styles.metadata}>{type}</div>
    {metaData &&
        <div className={styles.metadata}>{metaData}</div>}
    </div>
  );
}

const AssetHeader = ({ asset, isDarkMode, analytics, canDownload, canShare, onDownload, onClose, debug }) => {
  const name = getContentForFieldSelector(asset, 'name');
  const downloadUrl = _.get(asset, 'content.asset.downloadUrl');
  const content_type = _.get(asset, 'content.asset.metadata.content_type');

  const [showDropdown, setShowDropdown] = useState(false);

  const removeQueryParamsFromURL = (url) => {
    const newUrl = new URL(url);
    return `${newUrl.origin}${newUrl.pathname}`;
  }

  const assetAlias = `${_.get(asset, 'content.domain','')}${_.get(asset, 'url', '')}`;
  const isLayout = _.includes(_.get(asset, 'uid'), 'layout');
  const assetUrl = isLayout ?  assetAlias : removeQueryParamsFromURL(window.location.href);

  const copyAssetUrl = (url) => {
    const input = document.createElement('input');
    input.value = url;
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    input.select();

    document.execCommand('copy');
    document.body.removeChild(input);
  };

  const handleSocialShare = (type) => {
      let utmLink = assetUrl + "?" + new Date().getTime();
      utmLink = encodeURI(utmLink);

      let winTop = (screen.height - 500) / 2;
      let winLeft = (screen.width - 575) / 1.75;
      let url = '';

      switch (type) {
        case 'facebook':
          url = `https://www.facebook.com/sharer.php?u=${utmLink}&title=${name}`;
          break;
        case 'linkedIn':
          url = `https://www.linkedin.com/shareArticle?mini=true&url=${utmLink}&title=${name}`;
          break;
        case 'twitter':
          url = `https://twitter.com/share?url=${utmLink}&title=${name}`;
          break;
      }
      window.open(url, 'sharer', 'top=' + winTop + ',left=' + winLeft + ',toolbar=0,status=0,width=' + 500 + ',height=' + 575);
  }

  return (
    <div className={cx(styles.header, isDarkMode ? styles.darkHeader : styles.lightHeader)}>
      <div className={styles.details}>
        <div className={cx(styles.name, isDarkMode ? styles.darkName : styles.lightName)}>{name}</div>
        <ContentMetadata content={asset} isDarkMode={isDarkMode}/>
      </div>
      <div className={styles.actions}>
        <div className={styles.column1}>
          {debug &&
            <Button className={cx(styles.action, styles.debug)}>
              <AnalyticsDebug asset={asset?.content?.asset} analytics={analytics} isDarkMode={isDarkMode} lightBox={true}/>
            </Button>
          }
          <React.Fragment>
          {canShare && <Dropdown 
              className={cx(styles.action, styles.shareDropdown)} 
              show={showDropdown} 
              onToggle={() => setShowDropdown(!showDropdown)} 
              onMouseLeave={() => setShowDropdown(false)}
            >
              <Dropdown.Toggle id="dropdown-custom-components">
                <i className={cx('cleverstory-share')} />
                <span>Share</span>
                <i className={cx(styles.arrow, 'cleverstory-drop-down')} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={()=>copyAssetUrl(assetUrl)}><i className={cx('cleverstory-link')}/> 
                  Copy Link
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleSocialShare('facebook')}>
                  <i className={cx('cleverstory-facebook')} />Facebook
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleSocialShare('linkedIn')}>
                  <i className={cx('cleverstory-linkedin')}/>LinkedIn
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleSocialShare('twitter')}>
                  <i className={cx('cleverstory-twitter')} />Twitter
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>}
            {downloadUrl && canDownload && content_type!="application/url" &&
              <Button className={cx(styles.action, styles.download)} onClick={() => { onDownload(asset) }}>
                <i className={cx('cleverstory-download')} />
                <span>Download</span>
              </Button>
            }
          </React.Fragment>
        </div>

        <div className={styles.column2}>
          <Button className={cx(styles.action, styles.close, styles.closeLightBox)} onClick={onClose}>
            <i className={cx('cleverstory-close')} />
          </Button>
        </div>
      </div>
    </div>
  )
}

const AssetView = ({
  asset, isDarkMode, preload, active, onLoad, onVisibilityChange, onClose,
}) => {

  const contentContext = React.useContext(Content.Context);
  const gatingContext = React.useContext(GatingContext);
  const widgetContext = React.useContext(WidgetsContext);

  const [initialised, setInitialised] = React.useState(preload);
  const [analytics, setAnalytics] = React.useState(null);
  const [gatingConfigurations, setGatingConfigurations] = useState(_.get(asset, 'content.gatingConfigurations', []));
  const [showDownload, setShowDownload] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const activeSession = useSelector(state => _.get(state, `content.analytics.${asset?.uid}.item.id`));
  const disableDownload = _.get(asset, 'content.disableDownload', false);
  const disableShare = _.get(asset, 'content.disableShare', false);

  const [activeAssetRef] = useDelayedInView({
    rootMargin: '-50% 0% -50% 0%',
    root: document.querySelector(`.${styles.assetViewerDialog}`),
    onChange: (visible) => {
      onVisibilityChange(asset, visible);
    }
  });

  useEffect(() => {
      const updatedItems = gatingConfigurations.filter(({id}) => !gatingContext.assetConfigurations.includes(id));
      setGatingConfigurations(updatedItems);
  }, [gatingContext.assetConfigurations])

  useEffect(() => {
    setShowDownload(!disableDownload && gatingConfigurations.length === 0);
    setShowShare(!disableShare && gatingConfigurations.length === 0)
  }, [])

  useEffect(() => {
    if (!disableDownload && gatingContext.disabled) {
      setShowDownload(true);
    }
  }, [gatingContext.disabled])

  useEffect(() => {
    if (!disableShare && gatingContext.disabled) {
      setShowShare(true);
    }
  }, [gatingContext.disabled])
  
  const [preloadAssetRef] = useDelayedInView({
    rootMargin: '-50% 0% -50% 0%',
    root: document.querySelector(`.${styles.assetViewerContent}`),
    skip: initialised,
    onChange: (visible) => {
      if (visible) {
        setInitialised(true);
      }
    }
  });

  const setRefs = React.useCallback(
    (node) => {
      activeAssetRef(node)
      preloadAssetRef(node)
    },
    [activeAssetRef, preloadAssetRef]
  );

  useEffect(() => {
    if (preload) {
      setInitialised(true);
    }
  }, [preload]);


  const handleViewerLoaded = () => {
    if (!onLoad) return;

    onLoad(asset.uid);
  };

  const handleDownload = (asset) => {
    if(disableDownload) {
      return;
    }

    const downloadUrl = _.get(asset, 'content.asset.downloadUrl');
    window.open(downloadUrl, "_blank");

    contentContext.onDownload(asset);
  }

  useEffect(() => {
    if(gatingContext.configuration) {
      return;
    }
    if(analytics) {
      triggerAssetGating(analytics)
    }
  }, [analytics])


  const triggerAssetGating = ({ duration, percentageRead }) => {
    if (gatingContext.preview) {
      return;
    }

    const assetType = ["application/url", "image/gif", "image/tiff", "image/png", "image/bmp", "image/jpeg", "image/svg+xml"];
    const isAssetContain = _.includes(assetType, _.get(asset, 'content.asset.metadata.content_type'))
    const effectivePercentage = (isAssetContain && duration > 10) ? 100 : percentageRead;

    gatingConfigurations.every(item => {
      if(!item.enabled) {
        return true;
      }

      var triggerValue = _.get(item, 'trigger.value', 0);
      var limit = duration;

      if (_.get(item, 'trigger.type', '') === "PERCENTAGE") {
        limit = effectivePercentage;
      }

      if (triggerValue <= limit) {
        var gating = _.get(item, 'gating.id', null);

        if (gating) {
          widgetContext.fetchGating(gating);
          gatingContext.triggerGating(_.get(item, 'trigger.type', ''), null, {
            ...item,
            target: {
              ...item.target,
              assetModal: true
            }
          });
          return false;
        }
      }

      return true;
    })
  }

  return (
    <div ref={setRefs} className={cx(styles.asset, styles.lightboxAsset)} data-asset={asset?.uid}>
      <AssetHeader
        asset={asset}
        isDarkMode={isDarkMode}
        analytics={analytics}
        canDownload={showDownload}
        canShare={showShare}
        onDownload={handleDownload}
        onClose={onClose}
        debug={contentContext.debug}
      />
      <div className={cx(styles.body, {
        [styles.initialised]: initialised,
      }, isDarkMode ? styles.darkBody : styles.lightBody)}>
        {initialised &&
          <AssetContentViewer
            asset={asset}
            isDarkMode={isDarkMode}
            lightBox={true}
            active={active}
            onLoad={handleViewerLoaded}
            onAnalyticsChange={(analytics) => {
              if (activeSession || active) {
                setAnalytics(analytics);
                contentContext.onAnalyticsChange(asset, analytics);
              }
            }}
            onDebugAnalytics={(analytics) => {
              if (contentContext.debug) {
                setAnalytics(analytics);
              }
            }}
          />
        }
      </div>
    </div>
  )
}